<template>
  <div class="bewertung-cell" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
    <span ref="badge" class="bewertung-badge" :style="badgeStyle">
      {{ formatted }}
    </span>

    <!-- Mouse events, damit man dort drauf hovern kann -->
    <div
      v-if="visible"
      ref="tooltip"
      class="bewertung-tooltip"
      :style="tooltipPosition"
      @mouseenter="onTooltipEnter"
      @mouseleave="onTooltipLeave"
    >
      <!-- Ladezustand -->
      <div v-if="loading && !tooltipData" class="tooltip-loading">
        <i class="fas fa-spinner fa-spin"></i>
      </div>
      <!-- Fehlerzustand -->
      <div v-else-if="error && !tooltipData" class="tooltip-loading">
        Bewertungen konnten nicht geladen werden.
      </div>

      <template v-if="tooltipData">
        <!-- HEADER-BEREICH -->
        <div class="tooltip-header">
          <div class="header-left">
            <div class="score">{{ formatNumber(tooltipData.durchschnittZufriedenheit) }}</div>
            <div class="score-details">
              <div class="stars">
                <span
                  v-for="n in 5"
                  :key="n"
                  class="star"
                  :class="starClass(n, tooltipData.durchschnittZufriedenheit)"
                >
                  ★
                </span>
              </div>
              <div class="review-count">{{ tooltipData.anzahlBewertungen }} Bewertungen</div>
            </div>
          </div>

          <div class="header-right">
            <div class="header-stat">
              <div class="stat-label">Empfehlung</div>
              <div
                class="stat-value"
                :style="{ color: getScoreColor(tooltipData.durchschnittWeiterempfehlungsrate) }"
              >
                {{ formatNumber(tooltipData.durchschnittWeiterempfehlungsrate) }}/10
              </div>
            </div>
            <div class="header-divider"></div>
            <div class="header-stat">
              <div class="stat-label">Wiederbuchung</div>
              <div
                class="stat-value"
                :style="{ color: getWiederbuchungColor(tooltipData.wuerdeErneutBuchenProzent) }"
              >
                {{ formatPercent(tooltipData.wuerdeErneutBuchenProzent) }}%
              </div>
            </div>
          </div>
        </div>

        <div class="tooltip-divider"></div>

        <!-- GRÜNDE-BEREICH -->
        <div v-if="tooltipData.gruende && tooltipData.gruende.length" class="positive-section">
          <div class="section-title">Besonders positiv bewertet:</div>
          <ul class="positive-list">
            <li v-for="(item, i) in tooltipData.gruende" :key="i">
              <span class="grund-text">{{ item.grund }}</span>
            </li>
          </ul>
          <div class="review-hint">
            <span class="review-hint-label">Achtung</span>: Bewertungen können auch optionale Leistungen, Unterkünfte, Reiseleiter oder jahreszeitspezifische Aspekte betreffen, die nicht für alle Termine gelten.
          </div>
        </div>

        <div v-if="tooltipData.gruende && tooltipData.gruende.length > 0" class="tooltip-divider"></div>

        <!-- DETAIL-BEWERTUNGEN -->
        <div v-if="detailRatings.length" class="ratings-section">
          <div class="section-title">Bewertungen</div>
          <div v-for="(rating, i) in detailRatings" :key="i" class="rating-row">
            <span class="rating-label">{{ rating.label }}</span>
            <div class="rating-bar-bg">
              <div
                class="rating-bar"
                :style="{ width: (rating.value / 10) * 100 + '%', background: getScoreColor(rating.value) }"
              ></div>
            </div>
            <span class="rating-value">{{ formatNumber(rating.value) }}</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import apiService from '@/core/common/services/api.service';

export default {
  data() {
    return {
      tooltipData: null,
      loading: false,
      error: false,
      visible: false,
      hoverTimeout: null,
      hideTimeout: null,
      tooltipPosition: {},
    };
  },
  computed: {
    value() {
      return this.params.value;
    },
    reisekuerzel() {
      return this.params.data?.reise?.reisekuerzel;
    },
    formatted() {
      if (this.value == null) return '-';
      return this.formatNumber(this.value);
    },
    badgeStyle() {
      if (this.value == null) return { cursor: 'default' };
      // Gerundeten Wert verwenden, damit Farbe zum angezeigten Wert passt
      const score = Math.round(this.value * 10) / 10;
      if (score >= 4) return { background: '#1BC5BD26', borderColor: '#1BC5BD', color: '#00786F' };
      if (score >= 3) return { background: '#FFA80026', borderColor: '#FFA800', color: '#D97706' };
      return { background: '#F64E6026', borderColor: '#F64E60', color: '#C10007' };
    },
    // Baut die Detail-Bewertungen als Array auf, filtert null-Werte raus
    detailRatings() {
      if (!this.tooltipData) return [];
      return [
        { label: 'Unterkünfte', value: this.tooltipData.durchschnittBewertungUnterkunft },
        { label: 'Programm', value: this.tooltipData.durchschnittBewertungProgramm },
        { label: 'Fahrzeug', value: this.tooltipData.durchschnittBewertungFahrzeug },
      ].filter(r => r.value != null);
    },
  },
  beforeDestroy() {
    // Aufräumen: Timeout canceln und Tooltip vom Body entfernen
    if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
    if (this.hideTimeout) clearTimeout(this.hideTimeout);
    if (this.$refs.tooltip && this.$refs.tooltip.parentNode === document.body) {
      document.body.removeChild(this.$refs.tooltip);
    }
  },
  methods: {
    onMouseEnter() {
      if (this.value == null) return;
      if (this.hideTimeout) clearTimeout(this.hideTimeout);
      // 200ms Verzögerung
      this.hoverTimeout = setTimeout(() => {
        this.visible = true;
        this.$nextTick(() => {
          // WICHTIG: Tooltip an document.body hängen, damit er aus dem AG Grid
          // DOM-Baum raus ist. Grund: AG Grid setzt "transform" auf Rows,
          // was "position: fixed" kaputt macht (fixed wird dann relativ zum
          // transformierten Element statt zum Viewport positioniert).
          if (this.$refs.tooltip) {
            document.body.appendChild(this.$refs.tooltip);
          }
          this.updatePosition();
        });
        // Daten nur einmal laden
        if (!this.tooltipData && this.reisekuerzel) {
          this.fetchData();
        }
      }, 200);
    },
    onMouseLeave() {
      if (this.hoverTimeout) clearTimeout(this.hoverTimeout);
      // Kurzes Delay, damit der User zum Tooltip wechseln kann
      this.hideTimeout = setTimeout(() => {
        this.visible = false;
      }, 100);
    },
    onTooltipEnter() {
      if (this.hideTimeout) clearTimeout(this.hideTimeout);
    },
    onTooltipLeave() {
      this.hideTimeout = setTimeout(() => {
        this.visible = false;
      }, 100);
    },
    updatePosition() {
      const badge = this.$refs.badge;
      const tooltip = this.$refs.tooltip;
      if (!badge) return;
      // getBoundingClientRect gibt die Position relativ zum Viewport zurück
      const rect = badge.getBoundingClientRect();
      const tooltipHeight = tooltip ? tooltip.offsetHeight : 300;
      const viewportHeight = window.innerHeight;

      // Tooltip rechts neben dem Badge positionieren
      let top = rect.top - 20;

      // Falls der Tooltip unten abgeschnitten würde: nach oben verschieben
      if (top + tooltipHeight > viewportHeight - 10) {
        top = viewportHeight - tooltipHeight - 10;
      }
      // Mindestens 10px vom oberen Rand
      if (top < 10) top = 10;

      this.tooltipPosition = {
        position: 'fixed',
        top: top + 'px',
        left: rect.right + 12 + 'px',
      };
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await apiService.get(`Reise/${this.reisekuerzel}/Bewertungen/Aggregation`);
        this.tooltipData = response.data?.result?.value;
        // Position neu berechnen, weil sich die Tooltip-Höhe nach dem Laden ändert
        // Nur wenn der Tooltip noch sichtbar ist
        if (this.visible) {
          this.$nextTick(() => this.updatePosition());
        }
      } catch (err) {
        console.error('Fehler beim Laden der Bewertungs-Aggregation:', err);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    formatNumber(val) {
      if (val == null) return '-';
      return Number(val).toLocaleString('de-DE', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
    },
    formatPercent(val) {
      if (val == null) return '-';
      return Math.round(val);
    },
    // Rechnet Score (0-10) auf Sterne (0-5) um und gibt die passende CSS-Klasse zurück
    // Rundung nach Schema: Nachkomma 0-2 → voller Stern, 3-6 → halber Stern, 7-9 → nächster voller Stern
    starClass(n, score) {
      const whole = Math.floor(score);
      const tenth = Math.round((score - whole) * 10);
      let stars;
      if (tenth <= 2) stars = whole;
      else if (tenth <= 6) stars = whole + 0.5;
      else stars = whole + 1;
      if (n <= stars) return 'star-full';
      if (n - 0.5 <= stars) return 'star-half';
      return 'star-empty';
    },
    // Farbcodierung für Werte auf einer Skala 1-10 (Empfehlung, Detail-Bewertungen, Progress-Bars)
    getScoreColor(val) {
      if (val == null) return '#3c4043';
      if (val >= 7) return '#1BC5BD';
      if (val >= 4) return '#FFA800';
      return '#F64E60';
    },
    // Farbcodierung für Wiederbuchung (Prozent)
    getWiederbuchungColor(val) {
      if (val == null) return '#3c4043';
      if (val >= 70) return '#1BC5BD';
      if (val >= 40) return '#FFA800';
      return '#F64E60';
    },
  },
};
</script>

<style scoped lang="scss">
/* BADGE */
.bewertung-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.bewertung-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 33px;
  height: 28px;
  border: 0.5px solid #bedbff;
  background: #eff6ff;
  border-radius: 8px;
  box-shadow: 0px 1px 2px -1px #0000001a, 0px 1px 3px 0px #0000001a;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

/* TOOLTIP */
.bewertung-tooltip {
  z-index: 99999;
  width: 420px;
  background: var(--surface);
  border: 0.8px solid var(--border);
  border-radius: var(--radius-sm);
  box-shadow: 0px 20px 25px -5px #0000001a, 0px 8px 10px -6px #0000001a;
  padding: 16px;
  font-family: 'Poppins', sans-serif;
}

.tooltip-loading {
  text-align: center;
  padding: 20px;
  color: #6a7282;
  font-size: 14px;
}

/* HEADER */
.tooltip-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.score {
  font-size: 24px;
  font-weight: 700;
  color: var(--text);
  line-height: 32px;
}

.score-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* STERNE */
.stars {
  display: flex;
  gap: 2px;
}

.star {
  font-size: 18px;
  -webkit-text-stroke: 0.5px;
  filter: drop-shadow(0 0 0.3px currentColor);
}

.star-full {
  color: var(--warning);
}

.star-half {
  color: var(--warning);
  opacity: 0.5;
}

.star-empty {
  color: var(--border);
}

.review-count {
  font-size: 12px;
  color: #6a7282;
  line-height: 16px;
}

/* EMPFEHLUNG / WIEDERBUCHUNG */
.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-stat {
  text-align: center;
}

.stat-label {
  font-size: 12px;
  color: #6a7282;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  margin-top: 2px;
}

.header-divider {
  width: 1px;
  height: 40px;
  background: var(--border);
}

/* TRENNLINIEN */
.tooltip-divider {
  height: 1px;
  background: var(--border);
  margin: 8px 0;
}

/* GRÜNDE */
.positive-section {
  margin-bottom: 16px;
}

.section-title {
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: #3c4043;
  margin-bottom: 8px;
}

.positive-list {
  list-style: disc;
  padding-left: 20px;
  margin: 0;

  li {
    font-size: 12px;
    color: var(--text-body);
    line-height: 16.5px;
    margin-bottom: 6px;
  }
}

.grund-text {
  font-weight: 400;
}

.review-hint {
  font-style: oblique;
  font-size: 11px;
  color: var(--text-body);
  line-height: 16.5px;
}

.review-hint-label {
  font-weight: 700;
}

/* DETAIL-BEWERTUNGEN (Progress Bars) */
.ratings-section .section-title {
  font-size: 14px;
  margin-bottom: 0;
}

.rating-row {
  display: flex;
  align-items: center;
  margin-top: 8px;
  gap: 10px;
}

.rating-label {
  font-size: 13px;
  color: var(--text-body);
  min-width: 90px;
  flex-shrink: 0;
}

.rating-bar-bg {
  flex: 1;
  height: 10px;
  background: var(--border);
  border-radius: 5px;
  overflow: hidden;
}

.rating-bar {
  height: 100%;
  border-radius: 5px;
}

.rating-value {
  font-size: 14px;
  font-weight: 600;
  color: #3c4043;
  min-width: 30px;
  text-align: right;
}
</style>
